import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  AlertColor,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "src/components/snackbar/useSnackbar";
import { authenticationService } from "src/services";
import { put } from 'src/services/httpClient.service';

const EditAppDialog = ({appId, handleClose, fetchApps}) => {
  console.log('selectedAppId', appId);

  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const {vendorId, franchiseId} = authenticationService.getVendorFranchiseIds();

  const API_URL = `${APIURL}/api/ott-apps`;
  
  const [editData, setEditData] = useState({
    id: appId?.id || "",
    applicationName: appId?.applicationName || "",
    packageName: appId?.packageName || "",
    appType: appId?.appType || "1",
    apk: appId?.apk || "",
    priority: appId?.priority || "1",
    access: appId?.access || "none",
    version: appId?.version || "none",
    link: appId?.link || "none",
    logo: appId?.logo || null,
  });

  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Update state when appId changes
  useEffect(() => {
    if (appId) {
      setEditData({
        id: appId.id || "",
        applicationName: appId.applicationName || "",
        packageName: appId.packageName || "",
        appType: appId.appType || "1",
        apk: appId.apk || "",
        priority: appId.priority || "1",
        access: appId.access || "none",
        version: appId.version || "none",
        link: appId.link || "none",
        logo: appId.logo || null,
      });
    }
  }, [appId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setEditData({
      ...editData,
      logo: file,
    });
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      
      // Create appData object with all fields
      const appData = {
        applicationName: editData.applicationName,
        packageName: editData.packageName,
        appType: editData.appType,
        apk: editData.apk,
        priority: editData.priority,
        access: editData.access,
        version: editData.version,
        link: editData.link,
        vendorId: vendorId,
      };
      
      const formData = new FormData();
      
      // Append appData as JSON string with proper content type
      formData.append("appData", new Blob([JSON.stringify(appData)], { type: "application/json" }));
      
      // Append logo file if changed
      if (editData.logo instanceof File) {
        formData.append("logo", editData.logo);
      }

      await put(`${API_URL}/${editData.id}`, formData);
      
      showSnackbar('App updated successfully', 'success');
      fetchApps();
      
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error updating app:', error);
      showSnackbar('Error updating app: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      id: "",
      applicationName: "",
      packageName: "",
      appType: "1",
      apk: "",
      priority: "1",
      access: "none",
      version: "none",
      link: "none",
      logo: null,
    });
    handleClose(); // Close the dialog
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Application Name */}
      <TextField
        fullWidth
        label="Application Name"
        name="applicationName"
        value={editData.applicationName}
        onChange={handleInputChange}
        margin="dense"
        required
      />
      
      {/* Package Name */}
      <TextField
        fullWidth
        label="Package Name"
        name="packageName"
        value={editData.packageName}
        onChange={handleInputChange}
        margin="dense"
        required
      />
      
      {/* App Type */}
      <FormControl fullWidth margin="dense">
        <InputLabel>App Type</InputLabel>
        <Select
          name="appType"
          value={editData.appType}
          onChange={handleInputChange}
          label="App Type"
        >
          <MenuItem value="1">Type 1</MenuItem>
          <MenuItem value="2">Type 2</MenuItem>
          <MenuItem value="3">Type 3</MenuItem>
        </Select>
      </FormControl>
      
      {/* APK Field */}
      <TextField
        fullWidth
        label="APK"
        name="apk"
        value={editData.apk}
        onChange={handleInputChange}
        margin="dense"
        placeholder="Enter APK URL or leave empty"
      />
      
      {/* Priority */}
      <FormControl fullWidth margin="dense">
        <InputLabel>Priority</InputLabel>
        <Select
          name="priority"
          value={editData.priority}
          onChange={handleInputChange}
          label="Priority"
        >
          <MenuItem value="1">1 (Highest)</MenuItem>
          <MenuItem value="2">2</MenuItem>
          <MenuItem value="3">3</MenuItem>
          <MenuItem value="4">4</MenuItem>
          <MenuItem value="5">5 (Lowest)</MenuItem>
        </Select>
      </FormControl>
      
      {/* Access */}
      <FormControl fullWidth margin="dense">
        <InputLabel>Access</InputLabel>
        <Select
          name="access"
          value={editData.access}
          onChange={handleInputChange}
          label="Access"
        >
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="public">Public</MenuItem>
          <MenuItem value="private">Private</MenuItem>
          <MenuItem value="restricted">Restricted</MenuItem>
        </Select>
      </FormControl>
      
      {/* Version */}
      <TextField
        fullWidth
        label="Version"
        name="version"
        value={editData.version}
        onChange={handleInputChange}
        margin="dense"
        placeholder="Enter version (e.g., 1.0.0)"
      />
      
      {/* Link */}
      <TextField
        fullWidth
        label="Link"
        name="link"
        value={editData.link}
        onChange={handleInputChange}
        margin="dense"
        placeholder="Enter URL link"
      />
      
      {/* Logo Upload */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Button variant="outlined" component="label">
          Upload New Logo
          <input
            type="file"
            accept="image/jpeg, image/png"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        {editData.logo && (
          <Box sx={{ mt: 2 }}>
            {editData.logo instanceof File ? (
              <p>Selected file: {editData.logo.name}</p>
            ) : (
              <Box>
                <p>Current Logo:</p>
                <img 
                  src={editData.logo} 
                  alt="Current Logo" 
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }} 
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', margin: '20px', flexDirection: "row-reverse", gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleUpdate}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditAppDialog;